import { useEffect, useRef } from "react";
import { Capacitor } from "@capacitor/core";
import { PushNotifications } from "@capacitor/push-notifications";
import { getPushPermission, markPushPermissionAsked, savePushDeviceToken, PushPlatform } from "src/api/pushPermission";

/**
 * Push notifications (iOS & Android Capacitor): ask once per platform, save token per platform,
 * and re-check when user enables notifications later in OS Settings so we persist the token in Mongo.
 * APNS token for iOS, FCM token for Android — stored separately in pushNotificationState.ios / .android
 */
export const usePushNotifications = (getAccessToken: () => Promise<string | null>, isAuthenticated: boolean) => {
  const hasRun = useRef(false);

  useEffect(() => {
    if (hasRun.current) return; // If the hook has already run, return
    if (!isAuthenticated) return; // If the user is not authenticated, return
    if (!Capacitor.isNativePlatform()) return; // Only run on iOS and Android (Capacitor native)

    hasRun.current = true;

    console.log("Capacitor.getPlatform():", Capacitor.getPlatform());
    const platform: PushPlatform = Capacitor.getPlatform() === "android" ? "android" : "ios";

    (async () => {
      try {
        const accessToken = await getAccessToken();
        if (!accessToken) return;

        const { pushPermissionAsked } = await getPushPermission(accessToken, platform);

        PushNotifications.addListener("registration", (ev) => {
          console.log(`Push registration token (${platform}):`, ev.value);
          savePushDeviceToken(accessToken, ev.value, platform).catch((err) =>
            console.error("Failed to save push device token:", err)
          );
        });
        PushNotifications.addListener("registrationError", (err) => {
          console.error("Push registration error:", err.error);
        });

        let permStatus = await PushNotifications.checkPermissions();

        if (!pushPermissionAsked) {
          if (permStatus.receive === "prompt") {
            permStatus = await PushNotifications.requestPermissions();
          }
          markPushPermissionAsked(accessToken, platform).catch((err) =>
            console.error("Failed to mark push permission as asked:", err)
          );
        }

        if (permStatus.receive === "granted") {
          await PushNotifications.register();
        }
      } catch (err) {
        console.error("Push notification setup error:", err);
      }
    })();
  }, [getAccessToken, isAuthenticated]);
};
